import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'src/utils/axios';
import type { SavedPrompt } from 'src/components/SupportCase/types';

/** What the server says when the sixth prompt is refused, or the fifth copy. */
interface SaveError {
  ok: boolean;
  detail?: string;
}

/** The server's `{error, detail}` envelope on a refusal. */
interface RefusalBody {
  detail?: string;
}

export interface SavedPromptsResult {
  prompts: SavedPrompt[];
  limit: number;
  isLoading: boolean;
  error: string | null;
  save: (body: string, title?: string) => Promise<SaveError>;
  remove: (id: number) => Promise<boolean>;
  rename: (id: number, title: string) => Promise<boolean>;
  reorder: (fromIndex: number, toIndex: number) => Promise<void>;
  markUsed: (id: number) => void;
  refetch: () => Promise<void>;
}

const URL = '/support/prompts/';

/**
 * The user's saved prompts (ENG-4220).
 *
 * One hook for both surfaces — the chip strip under the composer and the Saved
 * prompts tab — because two fetches of the same five rows on the same screen would
 * disagree the moment one of them saved something.
 *
 * `save` returns the server's own sentence rather than throwing: the cap and the
 * duplicate are expected answers with copy written for them, and the caller shows
 * that sentence inline.
 */
const useSavedPrompts = (enabled = true): SavedPromptsResult => {
  const [prompts, setPrompts] = useState<SavedPrompt[]>([]);
  const [limit, setLimit] = useState(5);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(URL);
      setPrompts((response.data.prompts || []) as SavedPrompt[]);
      setLimit((response.data.limit as number) || 5);
      setError(null);
    } catch {
      setError('We could not load your saved prompts.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      void refetch();
    }
  }, [enabled, refetch]);

  const save = useCallback(
    async (body: string, title?: string): Promise<SaveError> => {
      try {
        const response = await axios.post(URL, { body, title: title || '' });
        setPrompts((current) => [...current, response.data as SavedPrompt]);
        return { ok: true };
      } catch (err) {
        const body = (err as { response?: { data?: RefusalBody } })?.response
          ?.data;
        return {
          ok: false,
          detail: body?.detail || 'That prompt could not be saved.',
        };
      }
    },
    []
  );

  const remove = useCallback(async (id: number): Promise<boolean> => {
    try {
      await axios.delete(`${URL}${id}/`);
      setPrompts((current) => current.filter((prompt) => prompt.id !== id));
      return true;
    } catch {
      return false;
    }
  }, []);

  const rename = useCallback(
    async (id: number, title: string): Promise<boolean> => {
      try {
        const response = await axios.patch(`${URL}${id}/`, { title });
        setPrompts((current) =>
          current.map((prompt) =>
            prompt.id === id ? (response.data as SavedPrompt) : prompt
          )
        );
        return true;
      } catch {
        return false;
      }
    },
    []
  );

  /**
   * Move one prompt to another index and renumber the strip.
   *
   * Positions are the server's order, so every row whose index changed is patched.
   * Five rows at most, and the list is then taken from the server's answers rather
   * than reassembled locally — a local splice that disagrees with the server is a
   * strip that reorders itself on the next load.
   */
  const reorder = useCallback(
    async (fromIndex: number, toIndex: number): Promise<void> => {
      if (fromIndex === toIndex) {
        return;
      }
      const previous = new Map(
        prompts.map((prompt) => [prompt.id, prompt.position])
      );
      const next = [...prompts];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      setPrompts(next.map((prompt, index) => ({ ...prompt, position: index })));
      await Promise.all(
        next.map((prompt, index) =>
          previous.get(prompt.id) === index
            ? Promise.resolve()
            : axios.patch(`${URL}${prompt.id}/`, { position: index })
        )
      );
      await refetch();
    },
    [prompts, refetch]
  );

  const markUsed = useCallback((id: number) => {
    // Fire and forget: the count is a nicety, and a failed bump must not stop the
    // prompt from being sent.
    setPrompts((current) =>
      current.map((prompt) =>
        prompt.id === id
          ? { ...prompt, use_count: prompt.use_count + 1 }
          : prompt
      )
    );
    void axios.post(`${URL}${id}/use/`, {}).catch(() => undefined);
  }, []);

  return useMemo(
    () => ({
      prompts,
      limit,
      isLoading,
      error,
      save,
      remove,
      rename,
      reorder,
      markUsed,
      refetch,
    }),
    [
      prompts,
      limit,
      isLoading,
      error,
      save,
      remove,
      rename,
      reorder,
      markUsed,
      refetch,
    ]
  );
};

export default useSavedPrompts;
